import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import { Route } from 'react-router-dom';
import { APP_ROUTES } from '../shared/config/routes-config';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import { Pagination } from '../shared/components/core-ui/pagination/pagination.component';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import { getAllProperties } from '../PropertyModule/shared/services/data.service';
import { getAllAreas } from '../shared/services/data/lookups.data';
import { AreaUiModel } from '../shared/ui-models/area.ui-model';
import { PropertyUiModel } from '../shared/ui-models/property.ui-model';
import Modal from '../ui/Model';
import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import {
  MaintenanceRequestComment,
  MaintenanceRequestItem,
  MaintenanceRequestStatus,
  searchMaintenanceRequests,
  updateMaintenanceRequest
} from './shared/services/operations.data';
import { birdToast } from '../shared/components/core-ui/toast';
import { formatCellDate } from './helpers';

const statusOptions: { value: MaintenanceRequestStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' }
];

const OperationMaintenancePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [items, setItems] = useState<MaintenanceRequestItem[]>([]);
  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(20);
  const [listCount, setListCount] = useState(0);
  const [areaId, setAreaId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [areaOptions, setAreaOptions] = useState<{ label: string; value: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<
    { label: string; value: string; areaId?: string }[]
  >([]);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const [statusValue, setStatusValue] = useState<MaintenanceRequestStatus>('new');
  const [submittingComment, setSubmittingComment] = useState(false);

  const filteredProjectOptions = useMemo(
    () =>
      projectOptions.filter((option) => !areaId || String(option.areaId || '') === String(areaId)),
    [areaId, projectOptions]
  );

  const loadMaintenanceRequests = useCallback(
    (page = offset, perPage = limit, nextAreaId = areaId, nextProjectId = projectId) => {
      setLoading(true);
      setError(false);

      searchMaintenanceRequests(
        {
          ...(nextAreaId ? { areaId: nextAreaId } : {}),
          ...(nextProjectId ? { projectId: nextProjectId } : {})
        },
        page,
        perPage
      ).then((response) => {
        setLoading(false);

        if (isErrorInfo(response)) {
          setError(true);
          setItems([]);
          setListCount(0);
          return;
        }

        setItems(response.data || []);
        setListCount(response.count || 0);
      });
    },
    [areaId, limit, offset, projectId]
  );

  useEffect(() => {
    loadMaintenanceRequests(1, limit);
  }, []);

  useEffect(() => {
    getAllAreas('operation-module').then((response: AreaUiModel[] | any) => {
      if (!isErrorInfo(response)) {
        setAreaOptions(
          (response || [])
            .map((area: AreaUiModel) => ({
              label: area.name || '',
              value: area.id || ''
            }))
            .filter((option: { label: string; value: string }) => Boolean(option.value))
        );
      }
    });

    getAllProperties().then((response: PropertyUiModel[] | any) => {
      if (!isErrorInfo(response)) {
        setProjectOptions(
          (response || [])
            .map((project: PropertyUiModel) => ({
              label: project.name || '',
              value: project.id || '',
              areaId: project.areaId || ''
            }))
            .filter((option: { label: string; value: string; areaId?: string }) =>
              Boolean(option.value)
            )
        );
      }
    });
  }, []);

  const summary = useMemo(
    () => ({
      total: listCount,
      newCount: items.filter((item) => item.status === 'new').length,
      inProgressCount: items.filter((item) => item.status === 'in_progress').length,
      doneCount: items.filter((item) => item.status === 'done').length
    }),
    [items, listCount]
  );

  const selectedRequest = useMemo(
    () => items.find((item) => item.id === selectedRequestId) || items[0] || null,
    [items, selectedRequestId]
  );

  const selectedRequestIdentifier = selectedRequest?.id || null;
  const selectedRequestComments = selectedRequest?.comments || [];

  useEffect(() => {
    if (!selectedRequestId && items[0]?.id) {
      setSelectedRequestId(items[0].id);
    }
  }, [items, selectedRequestId]);

  const refreshSelectedRequest = useCallback(() => {
    if (!selectedRequestIdentifier) {
      return Promise.resolve();
    }

    return searchMaintenanceRequests({ id: selectedRequestIdentifier }, 1, 1).then((response) => {
      if (isErrorInfo(response) || !response.data?.[0]) {
        throw response;
      }

      const updatedItem = response.data[0];
      setItems((currentItems) =>
        currentItems.map((item) => (item.id === updatedItem.id ? updatedItem : item))
      );
      return updatedItem;
    });
  }, [selectedRequestIdentifier]);

  const openCommentModal = () => {
    setCommentValue('');
    setStatusValue(selectedRequest?.status || 'new');
    setCommentModalOpen(true);
  };

  const closeCommentModal = () => {
    if (submittingComment) return;
    setCommentModalOpen(false);
    setCommentValue('');
  };

  const handleRetryLoad = () => {
    loadMaintenanceRequests(offset, limit);
  };

  const handleSearch = () => {
    setOffset(1);
    loadMaintenanceRequests(1, limit);
  };

  const handleResetFilters = () => {
    setAreaId('');
    setProjectId('');
    setOffset(1);
    loadMaintenanceRequests(1, limit, '', '');
  };

  const handleAreaChange = (value: ValueType<{ value: string; label: string }, false>) => {
    const nextAreaId = value?.value || '';

    setAreaId(nextAreaId);
    setProjectId((currentProjectId) => {
      const projectStillAvailable = projectOptions.some(
        (option) =>
          option.value === currentProjectId &&
          (!nextAreaId || String(option.areaId || '') === String(nextAreaId))
      );

      return projectStillAvailable ? currentProjectId : '';
    });
  };

  const handleRowSelect = (requestId?: number | null) => {
    setSelectedRequestId(requestId || null);
  };

  const handleStatusSelect = (
    item: MaintenanceRequestItem,
    value: ValueType<{ value: MaintenanceRequestStatus; label: string }, false>
  ) => {
    const nextStatus = value?.value;
    if (!nextStatus) return;

    setSelectedRequestId(item.id || null);

    const promise = updateMaintenanceRequest(item.id || '', {
      status: nextStatus
    })
      .then((response) => {
        if (isErrorInfo(response)) {
          throw response;
        }
        return refreshSelectedRequest();
      })
      .then(() => {
        setItems((currentItems) =>
          currentItems.map((currentItem) =>
            currentItem.id === item.id ? { ...currentItem, status: nextStatus } : currentItem
          )
        );
      });

    birdToast.promise(promise, {
      title: 'Maintenance status',
      loadingMessage: 'Updating status...',
      successMessage: 'Status updated successfully',
      errorPermissionMessage: 'You do not have permission to update this status',
      errorFailedMessage: 'Failed to update status'
    });
  };

  const handleWriteCommentClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    requestId?: number | null
  ) => {
    event.stopPropagation();
    setSelectedRequestId(requestId || null);
    openCommentModal();
  };

  const handleModalStatusChange = (
    value: ValueType<{ value: MaintenanceRequestStatus; label: string }, false>
  ) => {
    setStatusValue(value?.value || 'new');
  };

  const handlePageChange = (newPage: number) => {
    setOffset(newPage);
    loadMaintenanceRequests(newPage, limit);
  };

  const handleItemsPerPage = (itemsPerPage: number) => {
    setLimit(itemsPerPage);
    setOffset(1);
    loadMaintenanceRequests(1, itemsPerPage);
  };

  const submitComment = () => {
    if (!selectedRequestIdentifier || (!commentValue.trim() && !statusValue)) {
      return;
    }

    setSubmittingComment(true);

    const promise = updateMaintenanceRequest(selectedRequestIdentifier, {
      status: statusValue,
      ...(commentValue.trim() ? { comment: commentValue.trim() } : {})
    })
      .then((response) => {
        if (isErrorInfo(response)) {
          throw response;
        }
        return refreshSelectedRequest();
      })
      .then(() => {
        closeCommentModal();
      });

    birdToast.promise(promise, {
      loadingMessage: 'Updating request...',
      successMessage: 'Request updated successfully',
      errorPermissionMessage: 'You do not have permission to update this request',
      errorFailedMessage: 'Failed to update request'
    });

    setSubmittingComment(false);
  };

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      <section className="sticky top-0 z-30 -mx-6 mb-6 flex flex-col gap-4 border-b border-gray-200 bg-creams-50 px-6 py-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-4xl font-recoleta font-semibold text-neutral-500">
            Maintenance Requests
          </h1>
          <p className="mt-1 text-base font-gilroy-medium text-neutral-400">
            {summary.total} total · {summary.newCount} new · {summary.inProgressCount} in progress ·{' '}
            {summary.doneCount} done
          </p>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:w-[520px]">
            <Select
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              options={areaOptions}
              placeholder="Select area"
              value={areaOptions.find((option) => option.value === areaId) ?? null}
              isClearable
              className="w-full"
              onChange={handleAreaChange}
            />
            <Select
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              options={filteredProjectOptions}
              placeholder="Select project"
              value={filteredProjectOptions.find((option) => option.value === projectId) ?? null}
              isClearable
              className="w-full"
              onChange={(
                value: ValueType<{ value: string; label: string; areaId?: string }, false>
              ) => {
                setProjectId(value?.value || '');
              }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleSearch}
              type={ButtonType.primary}
              size={ButtonSize.small}
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Search
            </Button>
            <Button
              onClick={handleResetFilters}
              type={ButtonType.primaryInverted}
              size={ButtonSize.small}
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Clear
            </Button>
          </div>
        </div>
      </section>

      <section className="mt-6 space-y-6">
        {loading ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <LoadingData />
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
            <p className="text-base font-gilroy-bold uppercase tracking-[0.22em] text-rose-500">
              Maintenance
            </p>
            <h2 className="mt-3 text-4xl font-recoleta text-neutral-500">
              Unable to load maintenance requests
            </h2>
            <p className="mt-3 max-w-xl text-lg font-gilroy-medium text-neutral-400">
              The maintenance queue is temporarily unavailable. Retry to fetch the latest requests.
            </p>
            <Button
              onClick={handleRetryLoad}
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="mt-6"
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-10 shadow-sm">
            <NotFoundData />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="w-full overflow-x-auto">
                <table className="min-w-[1320px] w-full border-separate border-spacing-0">
                  <thead className="bg-white sticky top-0 z-10">
                    <tr className="[&>th]:text-lg [&>th]:font-medium [&>th]:py-4 [&>th]:font-gilroy-bold [&>th]:uppercase [&>th]:tracking-[0.12em] text-neutral-900 border-b border-gray-200">
                      <th className="px-4 text-left whitespace-nowrap">Id</th>
                      <th className="px-4 text-left whitespace-nowrap">Unit</th>
                      <th className="px-4 text-left whitespace-nowrap">Request Date</th>
                      <th className="px-4 text-left whitespace-nowrap">Status</th>
                      <th className="px-4 text-left whitespace-nowrap">Comments</th>
                      <th className="px-4 text-left whitespace-nowrap">Description</th>
                      <th className="px-4 text-left whitespace-nowrap">Created by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const isSelected = selectedRequest?.id === item.id;

                      return (
                        <tr
                          key={item.id || `${item.reservationId}-${item.requestDate}`}
                          onClick={() => handleRowSelect(item.id || null)}
                          className={`cursor-pointer  transition-colors ${
                            isSelected ? 'bg-primary-50/60' : 'hover:bg-creams-50'
                          }`}
                        >
                          <td className="border-t border-gray-100 px-4 py-4 align-center  ">
                            <div className="max-w-[360px]">
                              <p className="text-xl font-gilroy-bold text-neutral-500">
                                {item.id || '—'}
                              </p>
                            </div>
                          </td>

                          <td className="border-t border-gray-100 px-4 py-4 align-center  ">
                            <div>
                              <p className="text-xl font-gilroy-bold text-neutral-500">
                                {item.unitShortName || '—'}
                              </p>
                              <p className="mt-1 text-lg font-gilroy-medium text-neutral-400">
                                ID: {item.unitId || '—'}
                              </p>
                            </div>
                          </td>
                          <td className="border-t border-gray-100 px-4 py-4 align-center text-xl text-neutral-500">
                            {formatCellDate(item.requestDate)}
                          </td>

                          <td className="border-t border-gray-100 px-4 py-4 align-center  ">
                            <Select
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              value={statusOptions.find((option) => option.value === item.status)}
                              options={statusOptions}
                              classNamePrefix="react-select"
                              onChange={(value) => handleStatusSelect(item, value)}
                            />
                          </td>
                          <td className=" px-4 py-4 align-center">
                            <button
                              type="button"
                              onClick={(event) => handleWriteCommentClick(event, item.id || null)}
                              className="group inline-flex min-w-[170px] items-center justify-between gap-3 rounded-2xl border border-primary-200 bg-white px-4 py-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50"
                            >
                              <span className="flex min-w-0 items-center gap-3">
                                <span className="inline-flex h-10 min-w-[2.5rem] items-center justify-center rounded-xl bg-primary-50 px-3 text-sm font-gilroy-bold text-primary-500">
                                  {item.comments?.length || 0}
                                </span>
                                <span className="flex flex-col">
                                  <span className="text-xs font-gilroy-bold uppercase tracking-[0.12em] text-primary-400">
                                    Comments
                                  </span>
                                  <span className="text-sm font-gilroy-bold text-neutral-500">
                                    View or add
                                  </span>
                                </span>
                              </span>
                              <span className="text-lg font-gilroy-bold text-primary-400 transition-transform group-hover:translate-x-1">
                                →
                              </span>
                            </button>
                          </td>
                          <td className="border-t border-gray-100 px-4 py-4 align-center  ">
                            <div className="max-w-[280px]">
                              <p className="max-h-[3rem] overflow-hidden text-xl font-gilroy-medium leading-6 text-neutral-500 whitespace-pre-wrap">
                                {item?.description}
                              </p>
                            </div>
                          </td>
                          <td className="border-t border-gray-100 px-4 py-4 align-center   text-xl font-gilroy-regular text-neutral-500">
                            {item.createdByUsername || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {listCount > 0 && (
              <div className="fixed bottom-2 inset-x-0 mx-auto w-[92%] lg:w-[62%] flex justify-center items-center">
                <Pagination
                  itemCounts={listCount}
                  itemsPerPage={limit}
                  offset={offset}
                  handlePageChange={handlePageChange}
                  handleItemsPerPage={handleItemsPerPage}
                />
              </div>
            )}
          </>
        )}
      </section>

      <Modal
        isOpen={commentModalOpen}
        onClose={closeCommentModal}
        containerClassName="lg:h-fit lg:max-h-[80vh] lg:w-[640px]"
        headerContent={
          <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
            Add Comment
          </div>
        }
        footerContent={
          <div className="flex justify-end gap-4">
            <Button
              onClick={submitComment}
              size={ButtonSize.medium}
              loading={submittingComment}
              disabled={!selectedRequestIdentifier}
            >
              Send Comment
            </Button>
            <Button
              onClick={closeCommentModal}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
              disabled={submittingComment}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div className="w-full space-y-5">
          <div className="rounded-xl border border-gray-200 bg-creams-50 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-lg font-gilroy-bold uppercase  text-neutral-400">
                  Previous comments{' '}
                  {selectedRequestComments?.length > 0 && `(${selectedRequestComments.length})`}
                </p>
                <p className="mt-1 text-lg font-gilroy-medium text-neutral-500"></p>
              </div>
            </div>

            <div className="max-h-[260px] overflow-y-auto space-y-3 pr-1">
              {selectedRequestComments.length > 0 ? (
                selectedRequestComments.map((comment: MaintenanceRequestComment, index) => (
                  <div
                    key={`${comment.writtenById}-${index}`}
                    className="rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-[0_8px_24px_rgba(17,24,39,0.04)]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-lg font-semibold font-gilroy-medium text-neutral-500">
                          {comment.comment}
                        </p>
                        <p className="mt-1 text-lg font-gilroy-regular text-neutral-300">
                          {comment.writtenByUsername}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-6 text-center">
                  <p className="text-sm font-gilroy-medium text-neutral-400">
                    No previous comments yet.
                  </p>
                </div>
              )}
            </div>
          </div>

          <textarea
            value={commentValue}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setCommentValue(event.target.value)
            }
            autoFocus
            rows={6}
            placeholder="Write comment"
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-lg text-neutral-600 outline-primary-500 focus:border-primary-500"
          />
        </div>
      </Modal>
    </div>
  );
};

export const OperationMaintenanceModule = () => (
  <Route
    exact
    path={APP_ROUTES.OPERATION_MODULE.CHILDREN?.MAINTENANCE.PATH}
    component={OperationMaintenancePage}
  />
);
